import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { isPushSupported, subscribeToPush, savePushSubscription } from '../services/pushService';

const CLAVE_POSPUESTO = 'push_pospuesto_hasta';
const DIAS_ESPERA = 30;

// Aviso para activar las notificaciones. Existe porque pedir el permiso desde un
// useEffect está mal en dos frentes: en iOS, Notification.requestPermission()
// exige un gesto del usuario y desde un efecto la promesa se rechaza, dejando el
// permiso en 'default' para siempre; y en Chrome y Firefox, pedirlo nada más
// cargar y que el usuario lo descarte penaliza el origen, que puede acabar
// bloqueado para siempre. Aquí el permiso se pide desde el clic en el botón.
export default function AvisoNotificaciones() {
  const [visible, setVisible] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (!(await isPushSupported())) return;
      if (Notification.permission !== 'default') return; // ya concedido o denegado
      const hasta = parseInt(localStorage.getItem(CLAVE_POSPUESTO) || '0', 10);
      if (Date.now() < hasta) return;
      setVisible(true);
    })();
  }, []);

  const posponer = () => {
    localStorage.setItem(CLAVE_POSPUESTO, String(Date.now() + DIAS_ESPERA * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  const activar = async () => {
    setOcupado(true);
    setError('');
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== 'granted') {
        setVisible(false); // denegado: el navegador ya no volverá a preguntar
        return;
      }
      const sub = await subscribeToPush();
      await savePushSubscription(sub);
      sessionStorage.setItem('push_sincronizado', '1');
      setVisible(false);
    } catch {
      setError('No se pudieron activar aquí. Inténtalo desde Perfil → Notificaciones.');
    } finally {
      setOcupado(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="mx-4 mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-slate-800">
      <div className="flex items-start gap-3">
        <Bell className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-300" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-blue-900 dark:text-white">
            Activa las notificaciones
          </p>
          <p className="mt-0.5 text-xs text-blue-800 dark:text-slate-300">
            Te avisamos de vencimientos de tarjetas, límites de presupuesto y pagos programados.
          </p>
          {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button
              onClick={activar}
              disabled={ocupado}
              className="min-h-[44px] rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              {ocupado ? 'Activando…' : 'Activar'}
            </button>
            <button
              onClick={posponer}
              className="min-h-[44px] rounded-lg px-4 py-1.5 text-sm font-medium text-blue-700 transition hover:bg-blue-100 dark:text-blue-200 dark:hover:bg-slate-700"
            >
              Ahora no
            </button>
          </div>
        </div>
        <button onClick={posponer} aria-label="Cerrar" className="-m-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-blue-500 transition hover:text-blue-700 dark:text-slate-300 dark:hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
